<template>
  <b-modal
    v-model="visible"
    title="Hold transaction"
    ok-title="Hold sale"
    @ok="submit"
    @shown="onShown"
  >
    <p class="small text-muted mb-3">
      Add a short tip so you can find this sale quickly when the customer
      returns. Stock stays reserved until recall or cancel.
    </p>

    <b-form-group label="Easy tip to remember">
      <b-form-input
        class="form-control"
        ref="tipInput"
        v-model="label"
        maxlength="64"
        placeholder="e.g. Blue bag · Man in cap · Counter 2"
        @keyup.enter="submit"
      />
      <b-form-text>{{ label.length }}/64 characters</b-form-text>
    </b-form-group>

    <div class="hold-tip-section">
      <div class="hold-tip-section__title">Quick picks</div>
      <div class="hold-tip-chips">
        <button
          v-for="tip in quickTips"
          :key="tip"
          type="button"
          class="hold-tip-chip"
          :class="{ 'hold-tip-chip--active': label === tip }"
          @click="selectTip(tip)"
        >
          {{ tip }}
        </button>
      </div>
    </div>

    <div v-if="suggestedTips.length" class="hold-tip-section mt-3">
      <div class="hold-tip-section__title">Suggested for this cart</div>
      <div class="hold-tip-chips">
        <button
          v-for="tip in suggestedTips"
          :key="tip"
          type="button"
          class="hold-tip-chip hold-tip-chip--suggested"
          :class="{ 'hold-tip-chip--active': label === tip }"
          @click="selectTip(tip)"
        >
          {{ tip }}
        </button>
      </div>
    </div>

    <b-alert v-if="!label.trim()" show variant="light" class="mt-3 mb-0 small">
      No tip entered — this held sale will show as the sale number only.
    </b-alert>
    <b-alert v-else show variant="success" class="mt-3 mb-0 small">
      Will appear as: <strong>{{ label.trim() }}</strong>
    </b-alert>
  </b-modal>
</template>

<script>
const DEFAULT_QUICK_TIPS = [
  "Blue bag",
  "Red bag",
  "Black bag",
  "Man — cap",
  "Woman — glasses",
  "Child",
  "Counter pickup",
  "Phone order",
  "Be back soon",
  "Checking price",
];

export default {
  name: "HoldSaleModal",
  props: {
    value: { type: Boolean, default: false },
    customerName: { type: String, default: "" },
    customerPhone: { type: String, default: "" },
    itemCount: { type: Number, default: 0 },
    firstItemName: { type: String, default: "" },
  },
  data() {
    return {
      label: "",
      quickTips: DEFAULT_QUICK_TIPS,
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    suggestedTips() {
      const tips = [];

      if (this.customerName) {
        tips.push(this.customerName);
      }

      const phoneDigits = (this.customerPhone || "").replace(/\D/g, "");
      if (phoneDigits.length >= 4) {
        tips.push(`Phone …${phoneDigits.slice(-4)}`);
      }

      if (this.firstItemName) {
        const short =
          this.firstItemName.length > 22
            ? `${this.firstItemName.slice(0, 20)}…`
            : this.firstItemName;
        if (this.itemCount > 1) {
          tips.push(`${short} +${this.itemCount - 1}`);
        } else {
          tips.push(short);
        }
      }

      if (this.itemCount > 0) {
        tips.push(`${this.itemCount} item${this.itemCount === 1 ? "" : "s"}`);
      }

      return [...new Set(tips)].slice(0, 5);
    },
  },
  watch: {
    value(open) {
      if (open) this.label = "";
    },
  },
  methods: {
    onShown() {
      this.$nextTick(() => this.$refs.tipInput?.focus());
    },
    selectTip(tip) {
      this.label = tip;
      this.$refs.tipInput?.focus();
    },
    submit(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.$emit("confirm", this.label.trim());
      this.visible = false;
    },
  },
};
</script>

<style scoped>
.hold-tip-section__title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.hold-tip-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.hold-tip-chip {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 0.8rem;
  color: #334155;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.hold-tip-chip:hover {
  border-color: #1a7f4e;
  background: #f0fdf4;
}

.hold-tip-chip--suggested {
  border-color: #86efac;
  background: #f0fdf4;
}

.hold-tip-chip--active {
  border-color: #1a7f4e;
  background: #1a7f4e;
  color: #fff;
}
</style>
